import { increment } from "firebase/firestore";
import { firestore } from "@/services/firestore";
import { generic } from "@/repositories/generic";
import { addCreationData, createId } from "@/lib/database";
import { auth } from "@/services/auth";
import { Attachment } from "@/types/v2";

export const cardAttachmentsPath = (shopId: string, cardId: string) =>
  `shops/${shopId}/cards/${cardId}/attachments`;
export const cardPath = (shopId: string, cardId: string) =>
  `shops/${shopId}/cards/${cardId}`;

export const taskAttachmentsPath = (shopId: string, taskId: string) =>
  `shops/${shopId}/tasks/${taskId}/attachments`;
export const taskPath = (shopId: string, taskId: string) =>
  `shops/${shopId}/tasks/${taskId}`;

type NewAttachmentData = Pick<
  Attachment,
  "fileName" | "contentType" | "size" | "storagePath"
>;

export const attachmentRepository = {
  // entityPath: the parent Card/Task doc (for the atomic attachmentCount
  // increment). attachmentsPath: its /attachments subcollection. Both are
  // needed because the count lives on a different doc than the attachment
  // itself — see the spec's attachmentCount addendum.
  create: async (
    entityPath: string,
    attachmentsPath: string,
    data: NewAttachmentData
  ): Promise<Attachment> => {
    const id = createId();
    const attachment = addCreationData({ id, ...data }, "client") as Attachment;
    await firestore.batchWrite([
      { type: "set", path: `${attachmentsPath}/${id}`, data: attachment },
      {
        type: "update",
        path: entityPath,
        data: { attachmentCount: increment(1) },
      },
    ]);
    return attachment;
  },

  subscribe: (
    attachmentsPath: string,
    callback: (docs: Attachment[]) => void
  ) =>
    generic.subscribeCollection<Attachment>(
      attachmentsPath,
      callback,
      "_deleted",
      "!=",
      true
    ),

  delete: async (
    entityPath: string,
    attachmentsPath: string,
    id: string
  ): Promise<void> => {
    await firestore.batchWrite([
      {
        type: "update",
        path: `${attachmentsPath}/${id}`,
        data: {
          _deleted: true,
          _deletedBy: auth.currentUser?.uid ?? "UNKNOWN",
        },
      },
      {
        type: "update",
        path: entityPath,
        data: { attachmentCount: increment(-1) },
      },
    ]);
  },
};
